import axiosClient from "./axios-client/axiosClient.jsx";

class CompanyCardsAPI {
  getCompanyCards(token) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/card/credit-cards",
      headers,
    );
  }

  getCompanyCardInfo(token, cardId) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/card/" + cardId + "/full-info",
      headers,
    );
  }

  getCompanyCardSensitiveInfo(token, cardId, twoFACode) {
    const headers = {
      headers: {
        Authorization: `Bearer ${token}`,
        "X-2FA-Code": twoFACode || "",
      },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/card/" + cardId + "/info",
      headers,
    );
  }

  getKycStatus(token, email) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/kyc/status?email=" +
        encodeURIComponent(email),
      headers,
    );
  }

  getCompanyCardTransactions(token, cardId, startDate, endDate) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const requestBody = {
      startDate: startDate,
      endDate: endDate,
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/transactions",
      requestBody,
      headers,
    );
  }

  _buildCardBody(formData) {
    // Resolve mobile: prefer fullPhone (from config), else countryCode+mobile
    let mobile = null;
    if (formData.fullPhone) {
      mobile = formData.fullPhone;
    } else if (formData.countryCode && formData.mobile) {
      mobile = formData.countryCode + formData.mobile;
    }
    // Ensure mobile starts with +
    if (mobile && !mobile.startsWith("+")) {
      mobile = "+" + mobile;
    }

    // Mandatory fields — always included (ViaCarte requires these)
    const body = {
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      email: formData.email || "",
      occupation: formData.occupation || "",
      state: formData.state || "",
      countryId: (formData.countryId || "").toUpperCase(),
      nationality: (formData.nationality || "").toUpperCase(),
      city: formData.city || "",
      address1: formData.address1 || "",
      mobile: mobile || "",
      dob: formData.dob || "",
      name:
        formData.firstName && formData.lastName
          ? `${formData.firstName} ${formData.lastName}`
          : "",
    };

    // Optional fields — only include if present
    if (formData.title) body.title = formData.title;
    if (formData.suffix) body.suffix = formData.suffix;
    if (formData.address2) body.address2 = formData.address2;
    if (formData.postalCode) body.postalCode = formData.postalCode;
    if (formData.passCode) body.passCode = formData.passCode;
    if (formData.idType) body.idType = formData.idType;
    if (formData.idNumber) body.idNumber = formData.idNumber;
    if (formData.gender) body.gender = parseInt(formData.gender, 10);
    if (formData.alternateEmail) body.alternateEmail = formData.alternateEmail;
    if (formData.buildingNumber) body.buildingNumber = formData.buildingNumber;

    return body;
  }

  createCompanyCard(token, formData) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/generate",
      this._buildCardBody(formData),
      headers,
    );
  }

  createPhysicalCompanyCard(token, formData) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/generate-physical",
      this._buildCardBody(formData),
      headers,
    );
  }

  addFundsToCard(token, cardId, sourceCurrency, amount, notes) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const body = {
      notes: notes,
      amount: Number(amount),
      sourceCurrency: sourceCurrency,
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/add-funds",
      body,
      headers,
    );
  }

  transferFunds(token, cardId, cardTo, amount, notes) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const body = {
      cardTo: cardTo,
      clientNotes: notes,
      transferAmount: amount,
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/transfer-card",
      body,
      headers,
    );
  }

  removeFundsFromCard(token, cardId, amount, notes) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const body = {
      clientNotes: notes,
      transferAmount: Number(amount),
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/remove-funds",
      body,
      headers,
    );
  }

  deactivateCompanyCard(token, cardId) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };
    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/deactivate",
      {},
      headers,
    );
  }

  blockCompanyCard(token, cardId, newStatus, note, cvv) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const body = {
      newStatus: newStatus,
      note: note,
    };

    if (cvv) {
      body.cvv = cvv;
    }

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/block",
      body,
      headers,
    );
  }

  setCardPin(token, cardId, pin) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const body = {
      pin: pin,
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/set-pin",
      body,
      headers,
    );
  }

  updateNickname(token, cardId, nickname) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.post(
      "/credit-card-service/api/v1/card/" + cardId + "/nickname",
      { nickname: nickname },
      headers,
    );
  }

  getErrorCodes(token, lang) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/error-codes?lang=" +
        encodeURIComponent(lang || "eng"),
      headers,
    );
  }

  getTermsStatus(token, email) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return axiosClient.get(
      "/credit-card-service/api/v1/terms/status?email=" +
        encodeURIComponent(email),
      headers,
    );
  }

  getTerms(token) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };
    return axiosClient.get("/credit-card-service/api/v1/terms", headers);
  }

  acceptTerms(token, termsVersion) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const body = {
      termsVersion: termsVersion,
    };
    return axiosClient.post(
      "/credit-card-service/api/v1/terms/accept",
      body,
      headers,
    );
  }
}

export const companyCardsAPI = new CompanyCardsAPI();
